import { useMemo } from "react";

import { HStack } from "@chakra-ui/react";

import { Select } from "@tether-web-portal/components/common";
import { IDateRange, IOptionItem } from "@tether-web-portal/types/common";
import { MetricDataInterval, MetricDataIntervalTitleMap } from "@tether-web-portal/types/metricsTypes";
import { useMetricsInterval } from "@tether-web-portal/hooks";
import { DateRangePicker } from "@tether-web-portal/components/datepicker/DateRangePicker";
import dayjs from "@tether-web-portal/dayjs-setup";

interface MetricsIntervalSelectorProps {
  options: MetricDataInterval[];
}

export const MetricsIntervalSelector = ({ options }: MetricsIntervalSelectorProps) => {
  const { metricsCurrentDataInterval, setMetricsInterval, setCustomMetricsInterval, metricsTimeStamps } =
    useMetricsInterval();

  const { maxDate, minDate } = useMemo(
    () => ({
      maxDate: dayjs().endOf("day"),
      minDate: dayjs().subtract(10, "years").startOf("day"),
    }),
    [],
  );
  const timeFrameOptions = useMemo<IOptionItem[]>(
    () =>
      options.map((value) => ({
        value,
        label: MetricDataIntervalTitleMap[value],
      })),
    [options],
  );

  const handleTimeFrameChanged = (option: IOptionItem | null) => {
    if (!option) {
      return;
    }

    if (option.value === MetricDataInterval.CUSTOM) {
      setCustomMetricsInterval(metricsTimeStamps.fromTimestamp, metricsTimeStamps.toTimestamp);
    } else {
      setMetricsInterval(option.value as MetricDataInterval);
    }
  };

  const handleCustomDateRangeChange = (dateRange: IDateRange) => {
    if (dateRange.startDate) {
      const endDate = dateRange.endDate ? dayjs(dateRange.endDate) : dayjs(dateRange.startDate);
      setCustomMetricsInterval(dateRange.startDate.toISOString(), endDate.endOf("day").toISOString());
    }
  };

  return (
    <HStack alignItems="center" columnGap={2}>
      <Select
        options={timeFrameOptions}
        onChange={handleTimeFrameChanged}
        value={metricsCurrentDataInterval}
      />

      <DateRangePicker
        startDate={metricsTimeStamps.fromTimestamp}
        endDate={metricsTimeStamps.toTimestamp}
        showDateRangeOnButton
        onDatesChange={handleCustomDateRangeChange}
        maxSelectedDays={100}
        maxBookingDate={maxDate}
        minBookingDate={minDate}
        horizontalOffset={-390} // This is terrible, but entire daterange picker needs a re-write...
      />
    </HStack>
  );
};
